import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { format, parseISO, subDays, startOfDay, addDays } from "date-fns";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import type { CommunityStats as CommunityStatsType } from "../types";

const COLORS = ["#8b5cf6", "#06b6d4", "#10b981", "#f59e0b", "#ef4444"];

export function CommunityStats() {
  useEffect(() => {
    document.title = "vibe-check ⏵Community";
  }, []);
  const { isAuthenticated } = useAuth();
  const [stats, setStats] = useState<CommunityStatsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const data = await api.getCommunityStats();
      setStats(data);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load community stats",
      );
    } finally {
      setLoading(false);
    }
  };

  // Get unique users from the data
  const users = useMemo(() => {
    if (!stats?.top_users) return [];
    return stats.top_users.map((u) => u.user_name);
  }, [stats?.top_users]);

  // Transform daily activity by user into chart format
  const chartDataByUser = useMemo(() => {
    if (!stats?.daily_activity_by_user) return [];

    const dateMap = new Map<string, Record<string, string | number>>();

    // Generate all dates in the range (last 30 days)
    const today = startOfDay(new Date());
    const startDate = subDays(today, stats.period_days - 1);
    let currentDate = startDate;
    while (currentDate <= today) {
      const dateStr = format(currentDate, "yyyy-MM-dd");
      dateMap.set(dateStr, { rawDate: dateStr });
      currentDate = addDays(currentDate, 1);
    }

    // Collect all users
    const allUsers = new Set(
      stats.daily_activity_by_user.map((d) => d.user_name),
    );

    // Fill in user counts from the data
    stats.daily_activity_by_user.forEach((d) => {
      const existing = dateMap.get(d.date) || { rawDate: d.date };
      existing[d.user_name] = d.count;
      dateMap.set(d.date, existing);
    });

    // Fill in 0 for any users missing on a given day
    dateMap.forEach((entry) => {
      allUsers.forEach((user) => {
        if (entry[user] === undefined) entry[user] = 0;
      });
    });

    // Convert to array and format dates
    return Array.from(dateMap.values())
      .sort((a, b) => String(a.rawDate).localeCompare(String(b.rawDate)))
      .map((d) => ({
        ...d,
        date: format(parseISO(String(d.rawDate)), "MMM d"),
      }));
  }, [stats?.daily_activity_by_user, stats?.period_days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500 rounded-lg p-4">
        <p className="text-red-400">{error}</p>
        <button
          onClick={loadStats}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!stats) {
    return <p className="text-gray-400">No data available</p>;
  }

  return (
    <div className="space-y-8">
      {/* Promo banner for non-authenticated users */}
      {!isAuthenticated && (
        <Link
          to="/about"
          className="block bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg p-4 hover:from-purple-500 hover:to-purple-700 transition-all"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-semibold">
                Track your Claude Code conversations
              </p>
              <p className="text-purple-200 text-sm">
                Get Vibe-Check now - it's free and open source
              </p>
            </div>
            <span className="text-white text-2xl">&rarr;</span>
          </div>
        </Link>
      )}

      <div>
        <h1 className="text-2xl font-bold text-white">Community</h1>
        <p className="text-gray-400 mt-1">
          Aggregate statistics across all Vibe-Check users (last{" "}
          {stats.period_days} days)
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-800 rounded-lg p-6">
          <p className="text-gray-400 text-sm">Total Events</p>
          <p className="text-3xl font-bold text-white">
            {stats.totals.total_events.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-800 rounded-lg p-6">
          <p className="text-gray-400 text-sm">Active Users</p>
          <p className="text-3xl font-bold text-white">
            {stats.totals.total_users.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-800 rounded-lg p-6">
          <p className="text-gray-400 text-sm">Total Sessions</p>
          <p className="text-3xl font-bold text-white">
            {stats.totals.total_sessions.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Activity Chart */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-white mb-4">
          Daily Activity by User
        </h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartDataByUser}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
              <YAxis stroke="#9ca3af" fontSize={12} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#1f2937",
                  border: "1px solid #374151",
                  borderRadius: "8px",
                }}
                labelStyle={{ color: "#fff" }}
              />
              <Legend />
              {users.map((user, index) => (
                <Line
                  key={user}
                  type="linear"
                  dataKey={user}
                  name={user}
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-white mb-4">
          Top Contributors
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-700">
                <th className="pb-2 w-12">#</th>
                <th className="pb-2">User</th>
                <th className="pb-2 text-right">Events</th>
                <th className="pb-2 text-right">Sessions</th>
                <th className="pb-2 text-right">Active Days</th>
              </tr>
            </thead>
            <tbody>
              {stats.top_users.map((user, index) => (
                <tr
                  key={user.user_name}
                  className="border-b border-gray-700/50"
                >
                  <td className="py-3 text-gray-500 font-medium">
                    {index + 1}
                  </td>
                  <td className="py-3">
                    <span
                      className="inline-block w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-white font-medium">
                      {user.user_name}
                    </span>
                  </td>
                  <td className="py-3 text-right text-purple-400 font-medium">
                    {user.total_events.toLocaleString()}
                  </td>
                  <td className="py-3 text-right text-gray-400">
                    {user.total_sessions.toLocaleString()}
                  </td>
                  <td className="py-3 text-right text-gray-400">
                    {user.active_days}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {stats.top_users.length === 0 && (
            <p className="text-gray-500 text-center py-4">No users yet</p>
          )}
        </div>
      </div>

      {/* Cache Info */}
      <div className="text-center text-gray-500 text-sm">
        Data cached at {format(parseISO(stats.cached_at), "MMM d, h:mm a")}{" "}
        &middot; Refreshes {format(parseISO(stats.expires_at), "MMM d, h:mm a")}
      </div>
    </div>
  );
}
